import isEqual from 'lodash/isEqual';

import {
  CustomFieldAttributeValue,
  CustomFieldValuePatch,
  PatchApiArg,
} from 'apis/customFields';
import { CustomField, isCustomFieldValueEmpty } from 'utils/customFields';

/** Represents the arg of the custom field value patch. */
export type GetCustomFieldValuePatchArg = {
  /** The custom fields the form was built from. */
  readonly customFields: readonly CustomField[];
  /** The form values, keyed by attribute id. */
  readonly formValues: Record<string, unknown>;
  /** The ids of the attributes currently present in the form. */
  readonly visibleAttributeIds: readonly string[];
};

/**
 * Builds the update patch of a custom field value form.
 *
 * Attributes absent from the visible ids are treated as removed, so their
 * stored value lands in the before part with nothing to replace it. Attributes
 * whose value is unchanged are left out of the patch entirely, which lets the
 * caller tell an empty patch from a real update.
 */
export function getCustomFieldValuePatch({
  customFields,
  formValues,
  visibleAttributeIds,
}: GetCustomFieldValuePatchArg): PatchApiArg<CustomFieldValuePatch>['patch'] {
  const before: CustomFieldValuePatch = {};
  const after: CustomFieldValuePatch = {};

  customFields.forEach(
    ({ attribute: { id, data_type }, value: savedValue }) => {
      const formValue = visibleAttributeIds.includes(id)
        ? formValues[id]
        : undefined;
      const value: CustomFieldAttributeValue =
        data_type === 'string_list'
          ? {
              data_type,
              value: Array.isArray(formValue) ? (formValue.at(0) ?? []) : [],
            }
          : {
              data_type,
              value: typeof formValue === 'string' ? formValue : '',
            };
      const isEmpty = isCustomFieldValueEmpty(value);

      if (savedValue ? isEqual(savedValue.value, value.value) : isEmpty) {
        return;
      }

      if (savedValue) {
        before[id] = savedValue;
      }

      if (!isEmpty) {
        after[id] = value;
      }
    },
  );

  return { before, after };
}
